/**
 * LOAN OFFERS (UF-06). One request, multiple offers, compared side by side.
 *
 * This is the payoff of the two-sided marketplace: instead of a single approve or reject, the sponsor
 * receives competing offers from the DCPs the request reached, and compares them on the terms that
 * matter, rate, LTV, tenure, amount, before choosing. A DCP response can be more nuanced than yes or no
 * (indicative, conditional, more information needed), and the status says which.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, LipaApiError, type FinanceOffer } from '@/lib/api';
import { PageHeader } from '@/components/dash';
import { StatusPill } from '@/components/status';
import { EmptyState, ErrorState, LoadingCard } from '@/components/states';
import { Reveal } from '@/components/motion';

export default function Offers() {
  const [offers, setOffers] = useState<FinanceOffer[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setOffers((await api.finance.offers()).offers);
    } catch (e) {
      setError(e instanceof LipaApiError ? e.detail.message : 'We could not load your offers.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <ErrorState body={error} onRetry={() => void load()} />;
  if (!offers) return <LoadingCard lines={4} />;

  // Best rate, flagged so the eye lands on it. A lower rate is not always the best offer, so it is a
  // hint, not a verdict.
  const bestRate = offers.length ? Math.min(...offers.map((o) => o.interestPct)) : null;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <Link
        to="/credit"
        className="inline-flex items-center gap-1 text-sm text-ink-muted hover:text-ink"
      >
        <BackIcon /> Credit
      </Link>

      <PageHeader title="Offers" subtitle="Compare what providers have offered, then choose one." />

      {offers.length === 0 ? (
        <EmptyState
          title="No offers yet"
          body="When you request financing, eligible providers respond here with competing offers you can compare."
          action={{ label: 'Request financing', href: '/credit/apply' }}
        />
      ) : (
        <ul className="space-y-3">
          {offers.map((o) => (
            <li key={o.id}>
              <Reveal>
                <Link
                  to={`/credit/offers/${o.id}`}
                  className="block rounded-card border border-border bg-surface p-4 shadow-card transition hover:border-primary/40 hover:shadow-soft"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="truncate text-sm font-medium text-ink">{o.provider}</p>
                        {o.interestPct === bestRate ? (
                          <span className="rounded-pill bg-accent/15 px-2 py-0.5 text-nano font-semibold text-accent">
                            Lowest rate
                          </span>
                        ) : null}
                      </div>
                      <p className="truncate text-micro text-ink-subtle">{o.asset}</p>
                    </div>
                    <StatusPill status={o.status} />
                  </div>
                  <div className="mt-3 grid grid-cols-4 gap-3 border-t border-border pt-3">
                    <Spec label="Rate" value={`${o.interestPct}%`} accent />
                    <Spec label="Max LTV" value={`${o.maxLtvPct}%`} />
                    <Spec label="Tenure" value={`${Math.round(o.tenureMonths / 12)} yr`} />
                    <Spec label="Amount" value={`KES ${o.amount}`} />
                  </div>
                </Link>
              </Reveal>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Spec({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="min-w-0">
      <p className="text-nano text-ink-subtle">{label}</p>
      <p className={`tabular truncate text-sm font-medium ${accent ? 'text-brand' : 'text-ink'}`}>
        {value}
      </p>
    </div>
  );
}

function BackIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15 6l-6 6 6 6" />
    </svg>
  );
}
